/* eslint-disable array-bracket-newline, quotes */
import { useCallback, useEffect, useReducer } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import BigNumber from 'bignumber.js';
import { Address, erc20ABI } from 'wagmi';
import {
  prepareWriteContract,
  readContract,
  waitForTransaction,
  writeContract,
} from 'wagmi/actions';
import { parseEther } from 'viem';
import { ILimitOrderPayload, enums } from '@kattana-io/common';
import { calculateUSDValue } from '../../../../helpers/calculateUSDValue';
import { Direction, useLimitNativeToken } from '../../hooks';
import { actionCreateLimitOrder } from '../../../../actions/limit_orders';
import { BASE_TOKEN, SUPPORTED_CHAINS } from '../../helpers/nativeCoins';
import { getPriceForPair } from './../../helpers/statistics';
import { authTokenSelector } from '../../../../selectors/auth';
import useTokenBalance from '../../../../hooks/useTokenBalance';
import {
  getInitialLimitState,
  LimitActions,
  limitReducer,
  LimitTabs,
} from '../../reducers/dex/limitReducer';
import { LimitOrderAPI } from '../../../../store/createStore';
import { actionAppendTransaction } from '../../../../actions/transactions';
import { toIntermediateTransaction } from '../../../../helpers/intermediateTransactions';
import { ILimitProps } from '.';
import {
  ContractRoles,
  VOID_ADDRESS,
  getLimitDeployAddress,
} from '../../helpers/limitOrderRoles';
import { userWalletFactoryAbi } from '../../../../helpers/abis';
import { canGetEVMBalance } from '../../helpers/balances';
import { ETabs } from '../../reducers/dex/tradingReducer';

const MSG_CANT_CREATE = "Couldn't create a limit order";

const getExecutionType = (grid: boolean, tab: ETabs) => {
  if (tab === ETabs.WhaleLimit) {
    return enums.ExecutionType.TIME_DISTANCE;
  }
  if (grid) {
    return enums.ExecutionType.GRID;
  }
  return enums.ExecutionType.LIMIT;
};

/**
 * 0.1 is 10000
 * A pip is the smallest whole unit measurement of the difference between the bid and ask spread in a foreign exchange quote.
 * A pip equals 1/100 of 1%, or . 0001. Thus, the forex quote extends out to four decimal places.
 * @param slippage
 */
const convertSlippageToPips = (slippage: number): string =>
  String(Math.round(slippage * 100));

const useLimit = ({
  base,
  quote,
  meta,
  slippage,
  tab,
  onSetBaseAmount,
  onSetQuoteAmount,
  onNotify,
  onWalletRefresh,
  onSetSlippage,
  chainId,
  account,
  tradesCount,
  sendTokens,
  ratesAddress,
  tradingNetwork,
}: ILimitProps) => {
  const quoteAddress = meta?.flipped
    ? meta.base_address.toLowerCase()
    : meta?.quote_address.toLowerCase();

  const [state, dispatch] = useReducer(
    limitReducer,
    {
      baseAmount: base.amount,
      quoteAmount: quote.amount,
      meta,
      flipped: quoteAddress !== quote.address.toLowerCase(),
    },
    getInitialLimitState
  );

  const token = useSelector(authTokenSelector);
  const globalDispatch = useDispatch();

  const isSameChain = chainId === tradingNetwork.toDecimal();

  useEffect(() => {
    onSetSlippage(1);
  }, []);

  useEffect(() => {
    const price = getPriceForPair({ ticker: meta, quotePrice: true });
    let stopPrice = price;
    if (base.amount.isGreaterThan(0) && quoteAddress) {
      if (quoteAddress === quote.address.toLowerCase()) {
        stopPrice = quote.amount.div(base.amount).toNumber();
      } else {
        stopPrice = base.amount.div(quote.amount).toNumber();
      }
    }
    dispatch({
      type: LimitActions.SetCurrentPrice,
      payload: price,
    });
    dispatch({
      type: LimitActions.SetStopPrice,
      payload: stopPrice,
    });
    dispatch({
      type: LimitActions.SetGrid,
      payload: {
        initPrice: price,
        finalPrice: price,
      },
    });
  }, [meta]);

  useEffect(() => {
    let updatedPrice = new BigNumber(0);

    if (quoteAddress === quote.address.toLowerCase()) {
      updatedPrice = quote.amount.div(base.amount);
    } else {
      updatedPrice = base.amount.div(quote.amount);
    }
    if (updatedPrice.isFinite() && !updatedPrice.eq(state.stopPrice)) {
      dispatch({
        type: LimitActions.SetStopPrice,
        payload: updatedPrice.toNumber(),
      });
    }
  }, [quote.amount]);

  useEffect(() => {
    let updatedQuote = new BigNumber(0);

    if (quoteAddress === quote.address.toLowerCase()) {
      updatedQuote = base.amount.multipliedBy(state.stopPrice);
    } else {
      updatedQuote = base.amount.div(state.stopPrice);
    }

    if (updatedQuote.isFinite() && !updatedQuote.eq(quote.amount)) {
      onSetQuoteAmount(updatedQuote);
    }
  }, [base.amount, state.stopPrice]);

  useTokenBalance({
    address: account,
    token: base.address == BASE_TOKEN ? '' : base.address,
    chainId: tradingNetwork.toDecimal(),
    interval: 10000,
    enabled: canGetEVMBalance(tradingNetwork.toDecimal(), base, account || ''),
    onBalance(amount) {
      dispatch({
        payload: amount.toNumber(),
        type: LimitActions.SetBaseBalance,
      });
      const baseUSDValue = calculateUSDValue(
        base.address,
        amount,
        ratesAddress
      );
      dispatch({
        payload: baseUSDValue,
        type: LimitActions.SetBaseUSDBalance,
      });
    },
  });

  useTokenBalance({
    address: account,
    token: quote.address == BASE_TOKEN ? '' : quote.address,
    chainId: tradingNetwork.toDecimal(),
    interval: 10000,
    enabled: canGetEVMBalance(tradingNetwork.toDecimal(), quote, account || ''),
    onBalance(amount) {
      dispatch({
        payload: amount.toNumber(),
        type: LimitActions.SetQuoteBalance,
      });
      const quoteUSDValue = calculateUSDValue(
        quote.address,
        amount,
        ratesAddress
      );
      dispatch({
        payload: quoteUSDValue,
        type: LimitActions.SetQuoteUSDBalance,
      });
    },
  });

  const { fetchNativeTokenBalance } = useLimitNativeToken({
    proxyAddress: state.proxyWallet,
    chainId,
    setNativeToken: (payload) =>
      dispatch({ payload, type: LimitActions.SetNativeToken }),
  });

  /**
   * Rebuild tooltip when we change proxy
   */
  useEffect(() => {
    setTimeout(ReactTooltip.rebuild, 0);
  }, [state.limitTab]);

  const setLimitOrders = useCallback(async () => {
    const chId = chainId || 1;

    if (!SUPPORTED_CHAINS.includes(chId) || !account) {
      dispatch({
        payload: LimitTabs.NotSupported,
        type: LimitActions.SetLimitTab,
      });
      return;
    }

    try {
      console.log('limit orders: checking if wallet has proxy');
      const address = getLimitDeployAddress(
        chainId,
        ContractRoles.WalletFactory
      );
      if (!address) {
        return;
      }
      const proxyAddress = await readContract({
        address,
        abi: userWalletFactoryAbi,
        functionName: 'getUserWallet',
        args: [account as Address],
      });
      console.log('limit orders: result', proxyAddress);
      if (proxyAddress !== VOID_ADDRESS) {
        dispatch({
          payload: proxyAddress,
          type: LimitActions.SetProxyWallet,
        });

        dispatch({
          payload: LimitTabs.Available,
          type: LimitActions.SetLimitTab,
        });
      } else {
        dispatch({
          payload: LimitTabs.NotAvailable,
          type: LimitActions.SetLimitTab,
        });
      }
    } catch (e) {
      console.error('limit orders: error', e);
    }
  }, [account, chainId]);

  /**
   * Lets check if user has proxy
   */
  useEffect(() => {
    if (
      state.limitTab === LimitTabs.Available ||
      state.limitTab === LimitTabs.NotSupported ||
      state.limitTab === LimitTabs.NotAvailable
    ) {
      setLimitOrders();
    }
  }, [state.limitTab, setLimitOrders]);

  /**
   * Do estimate call to fetch quote value
   */
  const onBaseUpdate = (input: number) => {
    if (state.direction != Direction.BaseQuote) {
      dispatch({
        payload: Direction.BaseQuote,
        type: LimitActions.SetDirection,
      });
    }

    const newInput = new BigNumber(input);
    if (!base.amount.eq(newInput)) {
      onSetBaseAmount(newInput);
    }
  };

  const onQuoteUpdate = (input: number) => {
    if (state.direction != Direction.QuoteBase) {
      dispatch({
        payload: Direction.QuoteBase,
        type: LimitActions.SetDirection,
      });
    }

    const newInput = new BigNumber(input);
    if (!quote.amount.eq(newInput)) {
      onSetQuoteAmount(new BigNumber(input));
    }
  };

  const onCreateOrder = async () => {
    if (state.loading) {
      return;
    }
    let gt: boolean;
    if (state.isGrid) {
      gt = state.grid.finalPrice > state.grid.initPrice ? true : false;
    } else {
      gt = state.currentPrice < state.stopPrice ? true : false;
    }

    const executionType = getExecutionType(state.isGrid, tab);

    if (
      !meta ||
      (executionType === enums.ExecutionType.TIME_DISTANCE &&
        (!state.startDate || !state.endDate))
    ) {
      onNotify({
        kind: 'warning',
        message: 'Could not create limit order',
      });
      return;
    }

    if (base.amount.eq(0)) {
      onNotify({
        kind: 'warning',
        message: 'Could not create limit order with zero input',
      });
      return;
    }

    const timeDistanceMs =
      executionType === enums.ExecutionType.TIME_DISTANCE &&
      state.endDate!.getTime() - state.startDate!.getTime();

    const value: ILimitOrderPayload = {
      src: base.address,
      dest: quote.address,
      pair: meta.address,
      name: meta.name,
      price:
        executionType === enums.ExecutionType.GRID
          ? state.grid.initPrice.toFixed(8)
          : state.stopPrice.toFixed(8),
      price_usd: '0',
      gt,
      amount_in: base.amount.times(10 ** base.decimals).toFixed(),
      amount_out: quote.amount.times(10 ** quote.decimals).toFixed(),
      flipped: meta.flipped,
      proxy: state.proxyWallet,
      chain: meta.chain,
      exchange: meta.exchange,
      trigger_type: state.limitMode,
      slippage: convertSlippageToPips(slippage),
      trades_count: tradesCount,
      src_decimals: base.decimals,
      dest_decimals: quote.decimals,
      start_price: state.grid.initPrice.toFixed(8),
      start_price_usd: '0',
      end_price: state.grid.finalPrice.toFixed(8),
      end_price_usd: '0',
      execution_type: executionType,
      trades_time_distance: timeDistanceMs
        ? Math.round(timeDistanceMs / 1000)
        : undefined,
    };

    if (!account || !token) {
      return;
    }

    dispatch({ payload: true, type: LimitActions.SetLoading });

    const payload = {
      address: account,
      payload: value,
      chain_id: chainId,
    };

    const canCreate = await LimitOrderAPI.canCreate(account, token);

    if (!canCreate) {
      onNotify({ kind: 'warning', message: MSG_CANT_CREATE });
      dispatch({ payload: true, type: LimitActions.SetLoading });
      return;
    }

    /**
     * Send coin
     */
    if (base.address.toLocaleLowerCase() !== BASE_TOKEN && sendTokens) {
      try {
        console.log('Sending erc20 coin to proxy wallet');
        const amount = base.amount.times(10 ** base.decimals).toFixed();

        const { request: transferConfig } = await prepareWriteContract({
          address: base.address as Address,
          abi: erc20ABI,
          functionName: 'transfer',
          args: [state.proxyWallet as Address, BigInt(amount)],
        });
        const transfer = await writeContract(transferConfig);
        globalDispatch(
          actionAppendTransaction(
            toIntermediateTransaction(transfer.hash, chainId)
          )
        );
        const transferReceipt = await waitForTransaction(transfer);
        console.log('Info: ', transferReceipt);

        if (transferReceipt.status === 'reverted') {
          throw new Error('reverted');
        }
      } catch (e: any) {
        if (e.message === 'reverted') {
          onNotify({
            kind: 'error',
            message: 'Could not send token',
          });
        } else {
          onNotify({
            kind: 'error',
            message: e.message,
          });
        }
        console.warn(e);
        return;
      } finally {
        dispatch({ payload: false, type: LimitActions.SetLoading });
      }
    }

    globalDispatch(
      actionCreateLimitOrder({
        payload,
        executionType,
        callback: () => onWalletRefresh([base.address, quote.address]),
      })
    );
  };

  /**
   * Deploy function
   */
  const onDeployRequested = async () => {
    try {
      if (state.limitTab !== LimitTabs.Deploying) {
        dispatch({
          payload: LimitTabs.Deploying,
          type: LimitActions.SetLimitTab,
        });
        const address = getLimitDeployAddress(
          chainId,
          ContractRoles.WalletFactory
        );
        const target = getLimitDeployAddress(
          chainId,
          ContractRoles.TradingProtocol
        );

        if (!address || !target) {
          throw new Error('Address not found...');
        }

        const { request: deployConfig } = await prepareWriteContract({
          address,
          abi: userWalletFactoryAbi,
          functionName: 'deployUserWallet',
          args: [target, VOID_ADDRESS],
          value: parseEther('0.0'),
        });
        const deployTx = await writeContract(deployConfig);
        console.log('transaction object: ', deployTx);
        const receipt = await waitForTransaction(deployTx);
        console.log('transaction receipt: ', receipt);
        if (receipt.status === 'success') {
          dispatch({
            payload: LimitTabs.Available,
            type: LimitActions.SetLimitTab,
          });
          onNotify({
            kind: 'info',
            message: 'You have successfully deployed a proxy contract',
          });
        } else {
          onNotify({
            kind: 'error',
            message: 'Proxy contract was reverted',
          });
          dispatch({
            payload: LimitTabs.NotAvailable,
            type: LimitActions.SetLimitTab,
          });
        }
      } else {
        console.log('deploying already in progress, skipping');
      }
    } catch (err) {
      dispatch({
        payload: LimitTabs.NotAvailable,
        type: LimitActions.SetLimitTab,
      });
      onNotify({ kind: 'error', message: 'Could not deploy a proxy contract' });
      console.error(err);
    }
  };

  const quoteName =
    quote.address.toLowerCase() === quoteAddress ? quote.name : base.name;
  const basePriceUSD =
    base.address.toLowerCase() === meta?.base_address.toLowerCase()
      ? meta?.price_a_usd
      : meta?.price_b_usd;
  const quotePriceUSD =
    quote.address.toLowerCase() === meta?.quote_address.toLowerCase()
      ? meta?.price_b_usd
      : meta?.price_a_usd;

  const mergedState = {
    ...state,
    network: tradingNetwork,
    isSameChain,
    tradesCount,
    quoteName,
    basePriceUSD: basePriceUSD || 0,
    quotePriceUSD: quotePriceUSD || 0,
    onBaseUpdate,
    onQuoteUpdate,
    onCreateOrder,
    onDeployRequested,
    fetchNativeTokenBalance,
    dispatch,
  };

  return mergedState;
};

export default useLimit;
